<template>
  <div>
    <TitleBar :variant-class="'dashboard'">
      <template v-slot:title>
        <translate>Dashboard</translate>
      </template>
      <template v-slot:buttons>
        <OnboardingWizardLink />
        <OnboardingIntroLink :onboarding-intro="'onboarding_web_dashboard'" :open-welcome="true"/>
        <WidgetDropdown @add-widget="addWidget" @remove-widget="removeWidget" @reset-config="resetConfig" />
        <GekkoButton 
          :text="$gettext('New invoice')" 
          :href="newInvoiceUrl"
          icon="fa-solid fa-plus"
          class="mr-2"
        />
        <GekkoButton 
          :text="$gettext('New cost')" 
          :to="{name: 'AddCost'}"
          icon="fa-solid fa-plus"
        />
      </template>
    </TitleBar>

    <PromoModal v-if="showPromoModal" :show-prices="showBannerPrices" :promo-data="promoData" @close="showPromoModal = false" />
    <PromoBanner v-if="showPromoBanner && bannerTheme === 'blackfriday'" :banner-type="bannerType" :show-prices="showBannerPrices" :promo-data="promoData" @show-promo-modal="showPromoModal = true" />
    <PromoBannerHeroes v-if="showPromoBanner && bannerTheme === 'heroes'" :promo-data="promoData" :user-name="currentUser.display_name" @show-promo-modal="showPromoModal = true" />
    <PromoBannerEarly v-if="showPromoBanner && bannerTheme === 'early'" :promo-data="promoData" :user-name="currentUser.display_name" @show-promo-modal="showPromoModal = true" />
    <PromoBannerAprils v-if="showPromoBanner && bannerTheme === 'aprils'" :promo-data="promoData" :user-name="currentUser.display_name" @show-promo-modal="showPromoModal = true" />

    <!-- only for displaying delete error messages -->
    <DeleteAccountBanner v-if="deleteErrorMessage" :deleteErrorMessage="deleteErrorMessage" />

    <div v-if="isGrace" class="w-100 content-wrap dashboard" style="padding: 1.5rem 1rem;">
      <b-alert show class="gk-alert mb-0" variant="warning">
        <div class="grace-payment-alert">
          <p class="mb-0" v-translate='{ grace_ends: $options.filters.formatDate(graceDate) }'>
            Your payment was not successful. You will therefore be downgraded on %{grace_ends} unless you complete the Premium payment before that time.
          </p>
          <GekkoButton
            :href="Urls['subscriptions:manage']()"
            :text="$gettext('Go to Subscriptions')"
          />
        </div>
      </b-alert>
    </div>

    <div class="content dashboard">
      <div class="dashboard-grid">
        <GridContainer 
          v-if="initialDataLoaded"
          ref="gridcontainer"
          :key="gridKey"
          :dashboard-items="getUserWidgets"
          @save-config="debouncedSave"
        />
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapMutations } from "vuex";

import DashboardService from "@/store/services/dashboard.service.js";
import TitleBar from "@/components/TitleBar"
import GridContainer from "@/components/dashboard/GridContainer"
import PromoBanner from "@/components/dashboard/ui/PromoBanner"
import PromoBannerHeroes from "@/components/dashboard/ui/PromoBannerHeroes"
import PromoBannerEarly from "@/components/dashboard/ui/PromoBannerEarly"
import PromoBannerAprils from "@/components/dashboard/ui/PromoBannerAprils"
import PromoModal from "@/components/dashboard/ui/PromoModal"
import OnboardingIntroLink from "@/components/ui/OnboardingIntroLink"
import OnboardingWizardLink from "@/components/ui/OnboardingWizardLink"
import GekkoButton from "@/components/GekkoButton";
import WidgetDropdown from "@/components/dashboard/ui/WidgetDropdown"
import DeleteAccountBanner from "@/components/dashboard/ui/DeleteAccountBanner.vue";
import moment from 'moment';
import { debounce } from "@/utils/";

export default {
  name: "Dashboard",
  components: { TitleBar, GridContainer, OnboardingIntroLink, OnboardingWizardLink, GekkoButton, PromoBanner, PromoBannerHeroes, PromoBannerEarly, PromoBannerAprils, PromoModal, WidgetDropdown, DeleteAccountBanner },
  data () {
    return {
      currentConfig: '',
      currentStoreConfig: '',
      initialDataLoaded: false,
      gridKey: 0,
      maxWidgets: 12,
      showPromoBanner: false,
      bannerType: null,
      bannerTheme: null,
      promoData: null,
      showPromoModal: false,
      deleteErrorMessage: null,
      showBannerPrices: false
    }
  },
  computed: {
    ...mapGetters({ 
      getUserWidgets: "dashboard/getUserWidgets",
      currentCompany: 'main/getCurrentCompany',
      currentUser: 'main/getCurrentUser',
      getWidgetDefaults: "dashboard/getWidgetDefaults",
      getCompanyId: 'main/getCompanyId'
    }),
    newInvoiceUrl () {
      return window.Urls['invoices_add_invoice_step1']()
    },
    isGrace () {
      return this.currentUser.grace_date !== null
    },
    graceDate () {
      return this.currentUser.grace_date
    }
  },
  created () {
    this.loadAvailableUsers(this.getCompanyId)
    this.loadFinanciallyActiveYears(this.getCompanyId)
    this.loadDashboardConfig(this.currentCompany).then(() => {
      this.initialDataLoaded = true; 

      if(!this.$store.getters['getIsPremium'] || this.$route.query.promotest) {
        this.getPromoBanner()
      }

      // this.getPromoBanner()
      if(!this.$store.getters['getIsPremium']) {
        this.getPromoBannerBlackFriday()
      }
    });
    this.debouncedSave = debounce(config => {
      this.saveDashboard(config)
    }, 300)

    this.checkForInviteCodes()
    if (this.$route.name === 'DashboardDeleteAccount') {
      this.checkForDeletionProcedure()
    }
  },
  methods: {
    ...mapActions({
      loadAvailableUsers: "dashboard/loadAvailableUsers",
      loadFinanciallyActiveYears: "dashboard/loadFinanciallyActiveYears",
      loadDashboardConfig: "dashboard/loadDashboardConfig",
      saveDashboardConfig: "dashboard/saveDashboardConfig",
      resetToDefault: "dashboard/resetToDefault",
      loadUserCompanies: "main/loadUserCompanies",
      acceptInvitation: "notifications/acceptInvite",
      acceptAccountantInviteCode: "notifications/acceptAccountantInviteCode"
    }),
    ...mapMutations({ 
      setDashboardConfig: 'dashboard/SET_UPDATE_DASHBOARD_CONFIG',
      addStoreWidget: 'dashboard/ADD_WIDGET',
      deleteConfig: 'dashboard/DELETE_CONFIG',
    }),
    resetConfig () {
      this.resetToDefault();
      this.gridKey++
      this.$nextTick(() => {
        this.$refs.gridcontainer.saveCurrentDashboard('added')
      })
    },
    emptyDashboard () {
      this.deleteConfig()
      this.gridKey++
      this.$nextTick(() => {
        this.$refs.gridcontainer.saveCurrentDashboard('removed')
      })
    },
    getCurrentConfig () {
      this.currentConfig = this.$refs.gridcontainer.getDashboardCurrentConfig()
      this.currentStoreConfig = this.getUserWidgets
      console.log('Grid config ---------------------------------------')
      console.log(this.currentConfig)
      console.log('Store config --------------------------------------')
      console.log(this.currentStoreConfig)
    },
    addWidget (widget) {
      if (this.$refs.gridcontainer.getDashboardCurrentConfig().length < this.maxWidgets) {
        const widget_config = Object.assign({}, this.getWidgetDefaults(widget), {});
        this.addStoreWidget(widget_config)
      } else {
        this.$store.commit('setAlert', {
          title: this.$gettext('Too many widgets'),
          text: this.$gettext('The maximum number of widgets is ') + this.maxWidgets + '.',
          variant: "warning",
          duration: 4
        });
      }
    },
    removeWidget (widget) {
      this.$refs.gridcontainer.removeGridstackWidget(widget)
    },
    saveDashboard (config) {
      this.saveDashboardConfig(config)
    },
    getPromoBannerBlackFriday () {
      let from = '11/25/2024'
      let to = '11/29/2024'
      const promoDuration = 1

      if (this.$route.query.promotest) {
        if (this.$route.query.promotest === 'bf-monday') {
          from = '11/01/2024'
        } else if (this.$route.query.promotest === 'bf-thursday') {
          from = '11/01/2024'
          to = moment(new Date()).add(1, 'days').format('MM/DD/YYYY')
        } else if (this.$route.query.promotest === 'bf-friday') {
          from = '11/01/2024'
          to = moment(new Date()).format('MM/DD/YYYY')
        }
      }

      const dateFrom = new Date(from)
      const dateToday = new Date()
      const dateTo = new Date(to)
      dateTo.setDate(dateTo.getDate() + promoDuration) // careful as new Date() will set hour to 00:00:00

      // check if today is between start and end promo dates to show the banner
      if (dateFrom <= dateToday && dateToday < dateTo) {
        // check banner type based on pre-promo dates ('pre') or promoDay ('promo')
        this.bannerType = (dateToday < new Date(to)) ? 'pre' : 'promo'
        console.log('this.bannerType', this.bannerType)
        if (this.bannerType === 'promo') {
          DashboardService.discountForUser({ company_profile: this.getCompanyId }).then((response)=> {
            if (response.data.discount) {
              this.promoData = response.data
              this.showPromoBanner = true
            }
          })
        } else {
          this.showBannerPrices = moment().add(1, 'days').format('MM/DD/YYYY') === to
          if (this.currentCompany.country === 'BE') {
            this.promoData = {
              plan_cost_yearly: 12*14,
              discount: {
                benefit_type: "amount",
                amount: 73,
                end_date: to,
                from_date: from,
              }
            }
          } else {
            this.promoData = {
              plan_cost_yearly: 12*12,
              discount: {
                benefit_type: "amount",
                amount: 64,
                end_date: to,
                from_date: from,
              }
            }
          }
          
          this.showPromoBanner = true
        }
        this.bannerTheme = 'blackfriday'
      }
    },
    getPromoBanner () {
      DashboardService.discountForUser({ company_profile: this.getCompanyId }).then((response)=> {
        if (response.data.discount && response.data.discount.banner_type === "early_upgrade") {
          this.bannerTheme = 'early'
          this.promoData = response.data
          this.showPromoBanner = true
        }
        if (response.data.discount && response.data.discount.banner_type === "hero") {
          this.bannerTheme = 'heroes'
          this.promoData = response.data
          this.showPromoBanner = true
        }
        if (response.data.discount && response.data.discount.banner_type === "aprils_fools") {
          this.bannerTheme = 'aprils'
          this.promoData = response.data
          this.showPromoBanner = true
        }
        if(response.data.discount && response.data.discount.banner_type === "blackfriday"){
          this.promoData = response.data;
          let from = this.promoData.discount.start_date;// nov 20
          let to = this.promoData.discount.end_date;// nov 24
          const promoDuration = 1
          var dateFrom = new Date(from);// nov 20
          var dateToday = new Date();// nov 21
          var dateTo = new Date(to);// nov 24
          dateTo.setDate(dateTo.getDate() + promoDuration) // careful as new Date() will set hour to 00:00:00// nov 25
          // check if today is between start and end promo dates to show the banner
          if (dateFrom <= dateToday && dateToday < dateTo) {// nov 20 < nov 21 < nov 25
            // check banner type based on pre-promo dates ('pre') or promoDay ('promo')
            this.bannerType = (dateToday < new Date(to)) ? 'pre' : 'promo' // nov 21 < nov 25 => pre
            this.showPromoBanner = true
            this.bannerTheme = 'blackfriday'
          }
        }
      })
    },
    checkForInviteCodes () {
    // team_invite
      // ?next= is deprecated for these invites
      // if (this.$route.query.next && this.$route.query.next !== '') {
      //   if (this.$route.query.next.includes('dashboard/team-invite/')) {
      //     let uuid = this.$route.query.next.split('dashboard/team-invite/')
      //     uuid = uuid[1] || null // safeguard for missing uuid
      //     if (uuid) {
      //       this.acceptInvitation({ invite_id: uuid }).then(() => {
      //         this.loadUserCompanies()
      //       })
      //     }
      //   }
      // }

      // TEAM INVITE
      if (this.$route.query.invite_code && this.$route.query.invite_code !== '') {
        this.acceptInvitation({ invite_id: this.$route.query.invite_code }).then(() => {
          this.loadUserCompanies()
        })
      }
      // ACCOUNTANT INVITE
      if (this.$route.query.accountant_invite && this.$route.query.accountant_invite !== '') {
        let payload = {
          invite_id: this.$route.query.accountant_invite,
          params: {
            customer: this.currentCompany.id
          }
        }
        this.acceptAccountantInviteCode(payload).then(() => {
          this.loadUserCompanies()
        })
      }
    },
    checkForDeletionProcedure () {
      if (this.$route.params['uuid']) {
        if (this.currentUser.profile.deletion_procedure) {
          this.$store.commit('setAlert', {
            title: this.$gettext("Account Deletion"),
            text: this.$gettext("Your account has been marked for deletion"),
            variant: "warning", duration: 3
          })
        } else {
          DashboardService.checkDeleteToken({ delete_token: this.$route.params['uuid'] }).catch((error) => {
            if (error.status == 404) {
              this.deleteErrorMessage = this.$gettext('We could not find this token. Try copy/pasting the exact link.')
            } else if (error.status == 403) {
              this.deleteErrorMessage = this.$gettext('You are logged in to the wrong account. Please log in with the account for which this link was requested.')
            } else {
              this.deleteErrorMessage = this.$gettext('Unkown error')
            }
          })
        }
      }
    }
  }
}
</script>
<style lang="scss" scoped>

.content.dashboard {
  display: flex;
  padding: $gridGutter calc(1rem - #{$gridHalfGutter});
  padding-top: 0;
  .dashboard-grid {
    width: 100%;
  }
}

@media (max-width: '767px') {
  .content.dashboard {
    padding: $gridGutter 0;
    padding-top: 0;
  }
}


</style>

<style lang="scss">
.dashboard {
  .action-bar {
    flex-wrap: wrap;
    justify-content: flex-end;
    .btn { white-space: nowrap; }
    @media(max-width: '767px') {
      margin-top: 10px;
      .btn { margin-top: 10px; }
    }
  }
}
</style>
